import { useEffect, useState } from 'react';

import { Box, Button, IconButton } from '@mui/material';
import {
  Link as RouterLink,
  // useLocation,
  useNavigate
} from 'react-router-dom';
import MenuItem from '@mui/material/MenuItem';
import { clearAuth, getActiveRole, useAuth } from 'utils/auth';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import Select from 'components/common/Select';
import CustomLink from 'components/CustomLink';
import { currentRole, useSetCurrentRole } from 'pages/Login/state';
import LogoLight from 'static/logos/Logo.svg?react';
import api from 'utils/api';
import { useConstants, constantsAtom, useRefreshTokens } from 'utils/hooks';
import useWidth from 'utils/useWidth';
import { computedNavLinks } from './state';

// =============================================================================
const appbar_style = {
  display: 'flex',
  height: 94.5,
  alignItems: 'center'
};

const logo_style = {
  width: 116
};

// =============================================================================
interface LogoProps {
  variant: string | undefined;
  myCurrentRole: any;
}

const Logo = (props: LogoProps) => {
  const { CONSTANTS } = useConstants();

  function getLogo() {
    return <LogoLight style={logo_style} />;
  }

  let iconPath = '/';

  if (props.myCurrentRole === CONSTANTS.ROLE.ROLE_DEVELOPER_ADMIN) {
    iconPath = '/owners';
  } else if (props.myCurrentRole === CONSTANTS.ROLE.ROLE_DEVELOPER) {
    iconPath = '/instrument-on-boarding';
  }

  return (
    <IconButton
      component={RouterLink}
      disableRipple
      to={iconPath}
      key={props.myCurrentRole}
    >
      {getLogo()}
    </IconButton>
  );
};

// =============================================================================
interface AppbarProps {
  variant?: 'transparent' | 'admin' | 'default';
}

export default function Appbar(props: AppbarProps) {
  useRefreshTokens();
  const { CONSTANTS } = useConstants();

  const width = useWidth();
  const auth = useAuth();
  const myCurrentRole = useRecoilValue(currentRole);
  const setCurrentRole = useSetCurrentRole();
  const [currentMenu, setCurrentMenu] = useState(location.pathname);

  const setConstantsAtom = useSetRecoilState(constantsAtom);
  const navLinks = useRecoilValue(computedNavLinks);
  const navigate = useNavigate();

  const decoded = auth.user!;

  useEffect(() => {
    const currRole = getActiveRole()!;
    setCurrentRole(currRole);
    // Session refresh updates the role without changing the current page.
    setConstantsAtom(CONSTANTS);
  }, [CONSTANTS, auth.user]);

  useEffect(() => {
    setCurrentMenu(location.pathname);
  }, [location.pathname]);

  const handleClick = async () => {
    try {
      await api.post(auth.session ? '/public/oidc/logout' : '/api/logoutUser');
      clearAuth();
      navigate('/login');
    } catch (err: any) {
      console.error(err);
    }
  };

  function getPadding() {
    switch (width) {
      case 'tablet':
        return '0 48px';
      case 'desktop':
        return '0 96px';
      case 'mobile':
        return '0 24px';
      default:
        return '0 144px';
    }
  }

  const backgroundColor = () => {
    return 'blue.900';
  };

  const handleRole = (role: string) => {
    setCurrentRole(role);

    let rolePath = '/';

    if (role === CONSTANTS.ROLE.ROLE_DEVELOPER_ADMIN) {
      rolePath = '/owners';
    } else if (role === CONSTANTS.ROLE.ROLE_DEVELOPER) {
      rolePath = '/instrument-on-boarding';
    }
    navigate(rolePath);
  };

  const handleMenu = (menuItem: string) => {
    setCurrentMenu(menuItem);
  };

  function getMargin() {
    switch (width) {
      case 'tablet':
        return '0 0 0 16px';
      case 'laptop':
        return '0 0 0 24px';
      default:
        return '0 0 0 48px';
    }
  }

  return (
    <Box
      sx={{
        ...appbar_style,
        backgroundColor: backgroundColor(),
        padding: getPadding()
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Logo variant={props.variant} myCurrentRole={myCurrentRole} />

        <div className='app-bar menu'>
          <Select
            label='Menu'
            selected={currentMenu}
            menuProps={{
              PopoverClasses: {
                root: 'menu-list-root'
              }
            }}
            onChange={handleMenu}
            key={currentMenu}
          >
            {navLinks.map(({ label, link }, index) => {
              return (
                <MenuItem key={index} value={link}>
                  <CustomLink
                    key={index}
                    to={link}
                    variant='body_large_strong'
                    margin={getMargin()}
                  >
                    {label}
                  </CustomLink>
                </MenuItem>
              );
            })}
          </Select>
        </div>
      </div>
      <div style={{ flex: 1 }} />
      {decoded && decoded.roles.length && (
        <div className='app-bar'>
          <Select
            label='Current Role'
            selected={myCurrentRole}
            onChange={handleRole}
            key={myCurrentRole}
          >
            {decoded.roles
              .filter((role: string) => role !== CONSTANTS.ROLE.ROLE_USER)
              .map((role: string, i: number) => {
                const roleStr = role.split('_').splice(1).join(' ');
                return (
                  <MenuItem key={i} value={role}>
                    {roleStr}
                  </MenuItem>
                );
              })}
          </Select>
        </div>
      )}
      <Box sx={{ ml: 2 }}>
        <Button variant='primary' color='light' onClick={handleClick}>
          Log out
        </Button>
      </Box>
    </Box>
  );
}
